/**
 * useUpdates Hook - React hook for managing OTA updates
 */

import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../redux/reducers';
import * as UpdateActions from '../redux/actions/updateAction';
import { isOTAUpdatesEnabled, getOTAUpdateCheckInterval } from '../config/otaConfig';
import { IUpdateComponent } from '../models/Update';

const OTA_LOG_PREFIX = '[OTA useUpdates]';

export const useUpdates = () => {
  const dispatch = useDispatch();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState<number>(0);

  const updateState = useSelector((store: IStore) => store.update);
  const {
    availableUpdates,
    downloadProgress,
    updateStatus,
    currentVersion,
  } = updateState;

  // Check if updates are available
  const hasUpdates = availableUpdates.length > 0;

  // Check if any updates are ready to install
  const hasReadyUpdates = Object.values(downloadProgress).some(
    (progress) => progress.status === 'ready'
  );

  // Check if any updates are downloading
  const isDownloading = Object.values(downloadProgress).some(
    (progress) => progress.status === 'downloading'
  );

  // Count of mandatory updates
  const mandatoryUpdatesCount = availableUpdates.filter((u) => u.mandatory).length;

  /**
   * Check for updates
   */
  const checkForUpdates = useCallback((bypassRateLimit: boolean = false) => {
    console.log(`${OTA_LOG_PREFIX} checkForUpdates called (bypassRateLimit=${bypassRateLimit})`);
    try {
      // Check if OTA updates are enabled
      const enabled = isOTAUpdatesEnabled();
      console.log(`${OTA_LOG_PREFIX} OTA updates enabled: ${enabled}`);
      if (!enabled) {
        console.log(`${OTA_LOG_PREFIX} OTA updates are disabled, skipping check`);
        return;
      }

      // Check rate limiting
      const checkInterval = getOTAUpdateCheckInterval();
      const now = Date.now();
      const timeSinceLastCheck = now - lastCheckTime;
      console.log(`${OTA_LOG_PREFIX} Rate limit check: interval=${checkInterval}s, timeSinceLastCheck=${timeSinceLastCheck}ms`);

      if (!bypassRateLimit && timeSinceLastCheck < checkInterval * 1000) {
        console.log(`${OTA_LOG_PREFIX} Update check rate limited (${Math.round((checkInterval * 1000 - timeSinceLastCheck) / 1000)}s remaining)`);
        return;
      }

      console.log(`${OTA_LOG_PREFIX} Dispatching check updates request...`);
      setLastCheckTime(now);
      dispatch(UpdateActions.checkUpdates.request());
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error checking for updates:`, error);
    }
  }, [dispatch, lastCheckTime]);

  /**
   * Force check for updates (bypasses rate limiting) - for debugging
   */
  const forceCheckForUpdates = useCallback(() => {
    console.log(`${OTA_LOG_PREFIX} forceCheckForUpdates called (bypassing rate limit)`);
    checkForUpdates(true);
  }, [checkForUpdates]);

  /**
   * Download a specific update
   */
  const downloadUpdate = useCallback(
    (update: IUpdateComponent) => {
      console.log(`${OTA_LOG_PREFIX} downloadUpdate called for ${update.type} v${update.version}`);
      dispatch(UpdateActions.downloadUpdate.request(update));
    },
    [dispatch]
  );

  /**
   * Download all available updates
   */
  const downloadAllUpdates = useCallback(() => {
    console.log(`${OTA_LOG_PREFIX} downloadAllUpdates called, ${availableUpdates.length} updates available`);
    availableUpdates.forEach((update) => {
      const progress = downloadProgress[update.type];
      console.log(`${OTA_LOG_PREFIX} Checking ${update.type}: status=${progress?.status || 'none'}`);
      if (!progress || progress.status === 'failed') {
        console.log(`${OTA_LOG_PREFIX} Dispatching download for ${update.type} v${update.version}`);
        dispatch(UpdateActions.downloadUpdate.request(update));
      } else {
        console.log(`${OTA_LOG_PREFIX} Skipping ${update.type} (already ${progress.status})`);
      }
    });
  }, [dispatch, availableUpdates, downloadProgress]);

  /**
   * Cancel a download
   */
  const cancelDownload = useCallback(
    (updateType: string, version: string) => {
      console.log(`${OTA_LOG_PREFIX} cancelDownload called for ${updateType} v${version}`);
      dispatch(UpdateActions.cancelDownload(updateType, version));
    },
    [dispatch]
  );

  /**
   * Apply downloaded updates
   */
  const applyUpdates = useCallback(() => {
    console.log(`${OTA_LOG_PREFIX} applyUpdates called`);
    dispatch(UpdateActions.applyUpdates.request());
  }, [dispatch]);

  /**
   * Dismiss an optional update
   */
  const dismissUpdate = useCallback(
    (updateType: string) => {
      console.log(`${OTA_LOG_PREFIX} dismissUpdate called for ${updateType}`);
      dispatch(UpdateActions.dismissUpdate(updateType));
    },
    [dispatch]
  );

  /**
   * Show the update modal
   */
  const openUpdateModal = useCallback(() => {
    setShowUpdateModal(true);
  }, []);

  /**
   * Hide the update modal
   */
  const closeUpdateModal = useCallback(() => {
    setShowUpdateModal(false);
  }, []);

  /**
   * Auto-show modal when updates become available
   */
  useEffect(() => {
    if (hasUpdates && !showUpdateModal) {
      // Auto-show for mandatory updates
      if (mandatoryUpdatesCount > 0) {
        setShowUpdateModal(true);
      }
    }
  }, [hasUpdates, mandatoryUpdatesCount, showUpdateModal]);

  return {
    // State
    availableUpdates,
    downloadProgress,
    updateStatus,
    currentVersion,
    hasUpdates,
    hasReadyUpdates,
    isDownloading,
    mandatoryUpdatesCount,
    showUpdateModal,

    // Actions
    checkForUpdates,
    forceCheckForUpdates,
    downloadUpdate,
    downloadAllUpdates,
    cancelDownload,
    applyUpdates,
    dismissUpdate,
    openUpdateModal,
    closeUpdateModal,
  };
};
